import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { ShoppingCart, Plus, Edit, Trash2, CheckCircle2, Truck, Clock, DollarSign, Search } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;

const CATEGORIAS = ["general", "cocina", "barra", "snacks", "desechables", "decoracion", "limpieza", "proveedores_externos", "mobiliario", "personal"];
const UNIDADES = ["unidad", "kg", "g", "l", "ml", "pieza", "paquete", "caja", "hora"];
const ESTATUS = [
  { value: "pendiente", label: "Pendiente", color: "bg-amber-100 text-amber-800", icon: Clock },
  { value: "comprado", label: "Comprado", color: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
  { value: "entregado", label: "Entregado", color: "bg-emerald-100 text-emerald-800", icon: Truck },
];

const emptyForm = {
  producto: "",
  categoria: "general",
  unidad: "unidad",
  cantidad: 1,
  costo_unitario: 0,
  estatus: "pendiente",
  evento_id: "",
  proveedor_id: "",
  notas: "",
};

const Compras = () => {
  const [compras, setCompras] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);

  // Filtros
  const [search, setSearch] = useState("");
  const [filterEstatus, setFilterEstatus] = useState("__all__");
  const [filterCategoria, setFilterCategoria] = useState("__all__");
  const [filterEvento, setFilterEvento] = useState("__all__");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, e, p] = await Promise.all([
        axios.get(`${API}/compras`),
        axios.get(`${API}/compras/resumen`),
        axios.get(`${API}/eventos`),
        axios.get(`${API}/proveedores`).catch(() => ({ data: [] })),
      ]);
      setCompras(c.data || []);
      setResumen(r.data || null);
      setEventos(e.data || []);
      setProveedores(p.data || []);
    } catch {
      toast.error("Error al cargar compras");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const eventoNombre = (id) => {
    if (!id) return "—";
    const ev = eventos.find(e => e.evento_id === id);
    return ev ? `${ev.nombre || ev.tipo} · ${ev.fecha || ""}` : "Evento no encontrado";
  };

  const proveedorNombre = (id) => {
    if (!id) return "—";
    const p = proveedores.find(x => x.proveedor_id === id);
    return p ? p.nombre : "—";
  };

  const filtered = useMemo(() => {
    return compras.filter(c => {
      if (filterEstatus !== "__all__" && (c.estatus || "pendiente") !== filterEstatus) return false;
      if (filterCategoria !== "__all__" && (c.categoria || "general") !== filterCategoria) return false;
      if (filterEvento !== "__all__") {
        if (filterEvento === "__sin_evento__") {
          if (c.evento_id) return false;
        } else if (c.evento_id !== filterEvento) return false;
      }
      if (search) {
        const s = search.toLowerCase();
        const t = `${c.producto || ""} ${c.notas || ""}`.toLowerCase();
        if (!t.includes(s)) return false;
      }
      return true;
    });
  }, [compras, filterEstatus, filterCategoria, filterEvento, search]);

  const handleSave = async () => {
    if (!form.producto?.trim()) { toast.error("Producto requerido"); return; }
    try {
      const payload = {
        ...form,
        cantidad: Number(form.cantidad) || 1,
        costo_unitario: Number(form.costo_unitario) || 0,
        evento_id: form.evento_id || null,
        proveedor_id: form.proveedor_id || null,
      };
      if (editing) {
        await axios.put(`${API}/compras/${editing.compra_id}`, payload);
        toast.success("Compra actualizada");
      } else {
        await axios.post(`${API}/compras`, payload);
        toast.success("Compra creada");
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (c) => {
    setEditing(c);
    setForm({
      producto: c.producto || "",
      categoria: c.categoria || "general",
      unidad: c.unidad || "unidad",
      cantidad: c.cantidad || 1,
      costo_unitario: c.costo_unitario || 0,
      estatus: c.estatus || "pendiente",
      evento_id: c.evento_id || "",
      proveedor_id: c.proveedor_id || "",
      notas: c.notas || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/compras/${deleteId}`);
      toast.success("Compra eliminada");
      setDeleteId(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleEstatus = async (compra, nuevoEstatus) => {
    try {
      await axios.put(`${API}/compras/${compra.compra_id}`, { estatus: nuevoEstatus });
      toast.success(`Marcada como ${nuevoEstatus}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6" data-testid="compras-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-stone-900">Compras</h1>
          <p className="text-stone-500 text-sm mt-1">Compras transaccionales por evento o generales. Independiente del inventario de stock.</p>
        </div>
        <Button
          onClick={() => { setEditing(null); setForm(emptyForm); setDialogOpen(true); }}
          className="bg-emerald-900 hover:bg-emerald-800"
          data-testid="btn-nueva-compra"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva compra
        </Button>
      </div>

      {/* Tarjetas resumen */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-stone-200" data-testid="card-total-compras">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-stone-500 uppercase">Total compras</p>
              <ShoppingCart className="w-4 h-4 text-stone-400" />
            </div>
            <p className="font-display text-2xl text-stone-900 mt-1">{resumen?.total_items ?? 0}</p>
            <p className="text-xs text-stone-500 mt-1">{fmt(resumen?.total_monto)}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200" data-testid="card-pendientes">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-stone-500 uppercase">Pendientes</p>
              <Clock className="w-4 h-4 text-amber-600" />
            </div>
            <p className="font-display text-2xl text-amber-700 mt-1">{resumen?.pendientes?.cantidad ?? 0}</p>
            <p className="text-xs text-stone-500 mt-1">{fmt(resumen?.pendientes?.monto)}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200" data-testid="card-compradas">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-stone-500 uppercase">Compradas</p>
              <CheckCircle2 className="w-4 h-4 text-blue-600" />
            </div>
            <p className="font-display text-2xl text-blue-700 mt-1">{resumen?.compradas?.cantidad ?? 0}</p>
            <p className="text-xs text-stone-500 mt-1">{fmt(resumen?.compradas?.monto)}</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200" data-testid="card-entregadas">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-stone-500 uppercase">Entregadas</p>
              <Truck className="w-4 h-4 text-emerald-700" />
            </div>
            <p className="font-display text-2xl text-emerald-800 mt-1">{resumen?.entregadas?.cantidad ?? 0}</p>
            <p className="text-xs text-stone-500 mt-1">{fmt(resumen?.entregadas?.monto)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="border-stone-200">
        <CardContent className="p-3 grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Buscar producto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="input-search-compras"
            />
          </div>
          <Select value={filterEstatus} onValueChange={setFilterEstatus}>
            <SelectTrigger data-testid="filter-estatus"><SelectValue placeholder="Estatus" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los estatus</SelectItem>
              {ESTATUS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCategoria} onValueChange={setFilterCategoria}>
            <SelectTrigger data-testid="filter-categoria"><SelectValue placeholder="Categoría" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas las categorías</SelectItem>
              {CATEGORIAS.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterEvento} onValueChange={setFilterEvento}>
            <SelectTrigger data-testid="filter-evento"><SelectValue placeholder="Evento" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los eventos</SelectItem>
              <SelectItem value="__sin_evento__">Sin evento (general)</SelectItem>
              {eventos.map(ev => <SelectItem key={ev.evento_id} value={ev.evento_id}>{ev.nombre || ev.tipo} · {ev.fecha}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Lista de compras */}
      <Card className="border-stone-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-stone-500 text-sm">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center">
              <ShoppingCart className="w-10 h-10 mx-auto text-stone-300 mb-2" />
              <p className="text-stone-500 text-sm">No hay compras con los filtros aplicados.</p>
              <p className="text-stone-400 text-xs mt-1">Crea tu primera compra con el botón &quot;Nueva compra&quot;.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-stone-500 text-xs border-b border-stone-200">
                    <th className="px-4 py-3">Producto</th>
                    <th className="px-4 py-3">Categoría</th>
                    <th className="px-4 py-3">Evento</th>
                    <th className="px-4 py-3 text-right">Cantidad</th>
                    <th className="px-4 py-3 text-right">Costo unit.</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3">Estatus</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => {
                    const est = ESTATUS.find(e => e.value === (c.estatus || "pendiente")) || ESTATUS[0];
                    const Icon = est.icon;
                    return (
                      <tr key={c.compra_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`row-compra-${c.compra_id}`}>
                        <td className="px-4 py-2">
                          <p className="font-medium text-stone-900">{c.producto}</p>
                          {c.notas && <p className="text-xs text-stone-500 truncate max-w-xs">{c.notas}</p>}
                        </td>
                        <td className="px-4 py-2 text-stone-600 capitalize">{(c.categoria || "general").replace(/_/g, " ")}</td>
                        <td className="px-4 py-2 text-stone-600 text-xs">{eventoNombre(c.evento_id)}</td>
                        <td className="px-4 py-2 text-right text-stone-700">{c.cantidad} <span className="text-xs text-stone-400">{c.unidad}</span></td>
                        <td className="px-4 py-2 text-right text-stone-700">{fmt(c.costo_unitario)}</td>
                        <td className="px-4 py-2 text-right text-stone-900 font-medium">{fmt(c.costo_total)}</td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-medium ${est.color}`}>
                            <Icon className="w-3 h-3" /> {est.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            {(c.estatus || "pendiente") === "pendiente" && (
                              <Button size="sm" variant="ghost" onClick={() => handleEstatus(c, "comprado")} title="Marcar comprada" data-testid={`btn-comprar-${c.compra_id}`}>
                                <CheckCircle2 className="w-4 h-4 text-blue-700" />
                              </Button>
                            )}
                            {c.estatus === "comprado" && (
                              <Button size="sm" variant="ghost" onClick={() => handleEstatus(c, "entregado")} title="Marcar entregada" data-testid={`btn-entregar-${c.compra_id}`}>
                                <Truck className="w-4 h-4 text-emerald-700" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleEdit(c)} title="Editar" data-testid={`btn-edit-compra-${c.compra_id}`}>
                              <Edit className="w-4 h-4 text-stone-700" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setDeleteId(c.compra_id)} title="Eliminar" data-testid={`btn-del-compra-${c.compra_id}`}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-stone-50 border-t border-stone-200">
                    <td colSpan={5} className="px-4 py-3 text-right text-xs uppercase text-stone-500">Total filtrado</td>
                    <td className="px-4 py-3 text-right font-display text-base text-stone-900">
                      {fmt(filtered.reduce((s, c) => s + (c.costo_total || 0), 0))}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Compra */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="dialog-compra">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar compra" : "Nueva compra"}</DialogTitle>
            <DialogDescription className="text-xs">
              Las compras son transaccionales. Si quieres llevar stock manejable, usa el módulo Inventario.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Producto *</Label>
              <Input value={form.producto} onChange={(e) => setForm({ ...form, producto: e.target.value })} data-testid="compra-producto" />
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                <SelectTrigger data-testid="compra-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIAS.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unidad</Label>
              <Select value={form.unidad} onValueChange={(v) => setForm({ ...form, unidad: v })}>
                <SelectTrigger data-testid="compra-unidad"><SelectValue /></SelectTrigger>
                <SelectContent>{UNIDADES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cantidad *</Label>
              <Input type="number" step="0.01" min="0" value={form.cantidad} onChange={(e) => setForm({ ...form, cantidad: e.target.value })} data-testid="compra-cantidad" />
            </div>
            <div>
              <Label>Costo unitario *</Label>
              <Input type="number" step="0.01" min="0" value={form.costo_unitario} onChange={(e) => setForm({ ...form, costo_unitario: e.target.value })} data-testid="compra-costo" />
            </div>
            <div>
              <Label>Estatus</Label>
              <Select value={form.estatus} onValueChange={(v) => setForm({ ...form, estatus: v })}>
                <SelectTrigger data-testid="compra-estatus"><SelectValue /></SelectTrigger>
                <SelectContent>{ESTATUS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Evento (opcional)</Label>
              <Select value={form.evento_id || "__none__"} onValueChange={(v) => setForm({ ...form, evento_id: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Sin evento" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin evento (general)</SelectItem>
                  {eventos.map(ev => <SelectItem key={ev.evento_id} value={ev.evento_id}>{ev.nombre || ev.tipo} · {ev.fecha}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Proveedor (opcional)</Label>
              <Select value={form.proveedor_id || "__none__"} onValueChange={(v) => setForm({ ...form, proveedor_id: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Sin proveedor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin proveedor</SelectItem>
                  {proveedores.map(p => <SelectItem key={p.proveedor_id} value={p.proveedor_id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            </div>
            <div className="col-span-2 bg-stone-50 p-3 rounded text-sm flex items-center justify-between">
              <span className="text-stone-600 flex items-center gap-1"><DollarSign className="w-4 h-4" /> Total calculado</span>
              <span className="font-display text-lg text-stone-900">
                {fmt(Number(form.cantidad || 0) * Number(form.costo_unitario || 0))}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-guardar-compra">
              {editing ? "Guardar cambios" : "Crear compra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminación */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta compra?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La compra quedará eliminada permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirmar-eliminar-compra">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Compras;
